import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import Drawer from "./Drawer";
import { Menu, Home, Crown, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LANGUAGES, setUserLanguage, LANGUAGE_SWITCHER_ENABLED } from "../utils/language";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const choose = (code) => {
    setUserLanguage(i18n, code);
    setOpen(false);
  };

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl font-extrabold text-sm sm:text-base"
        style={{ background: "var(--surface)", color: "var(--text-primary)", border: "2px solid var(--border)" }}
        aria-label={t("header.languageAriaLabel")}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-switcher-btn"
      >
        <span aria-hidden="true">{current.flag}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 rounded-xl overflow-hidden z-40"
          style={{ background: "var(--surface)", border: "2px solid var(--border)", boxShadow: "0 4px 16px rgba(0,0,0,0.18)", minWidth: 130 }}
          role="listbox"
          data-testid="lang-switcher-menu"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => choose(l.code)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-left"
              style={{
                background: l.code === current.code ? "var(--secondary)" : "transparent",
                color: "var(--text-primary)",
              }}
              role="option"
              aria-selected={l.code === current.code}
              data-testid={`lang-switcher-option-${l.code}`}
            >
              <span aria-hidden="true">{l.flag}</span> {l.label}
              {l.code === current.code && <Check size={14} style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header({ title, subtitle, right }) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSubscribed = user?.subscription?.type === "subscription";

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${BACKEND_URL}/api/stripe/create-checkout`, {
        method: "POST",
        credentials: "include",
        headers: token ? { "Authorization": `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      alert(t("header.paymentError"));
    } finally { setSubscribing(false); }
  };

  return (
    <>
      <header
        className="sticky top-0 z-30"
        style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}
        data-testid="app-header"
      >
        <div className="vs-gradient-bar" />
        <div className="max-w-2xl mx-auto px-2 sm:px-3 py-3 flex items-center gap-1 sm:gap-2">
          {/* MENU button */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 rounded-xl font-extrabold text-xs"
            style={{
              background: "var(--gradient-vine)",
              color: "white",
              border: "2px solid var(--primary)",
              boxShadow: "0 2px 8px rgba(39, 92, 52, 0.25)",
            }}
            aria-label={t("header.menuAriaLabel")}
            data-testid="menu-btn"
          >
            <Menu size={16} strokeWidth={2.6} /> {t("header.menuButton")}
          </button>
          <Logo size={30} />
          <div className="flex-1 min-w-0">
            <div className="heading text-lg font-extrabold tracking-tight leading-tight truncate" style={{ color: "var(--text-primary)" }}>
              {title || "Il Mio Vigneto"}
            </div>
            {subtitle && (
              <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                {subtitle}
              </div>
            )}
          </div>

          {/* ABBONATI button — solo se non è già abbonato */}
          {!isSubscribed && (
            <button
              onClick={handleSubscribe}
              disabled={subscribing}
              className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-2 rounded-xl font-extrabold text-xs"
              style={{
                background: "linear-gradient(135deg, #b8860b, #d4a017)",
                color: "white",
                border: "2px solid #b8860b",
                boxShadow: "0 2px 8px rgba(184, 134, 11, 0.3)",
                whiteSpace: "nowrap",
              }}
              aria-label={t("header.subscribeAriaLabel")}
            >
              <Crown size={14} strokeWidth={2.6} />
              {subscribing ? "..." : t("header.premiumLabel")}
            </button>
          )}

          {LANGUAGE_SWITCHER_ENABLED && <LanguageSwitcher />}

          {/* HOME button */}
          <button
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 rounded-xl font-extrabold text-xs"
            style={{
              background: "var(--gradient-vine)",
              color: "white",
              border: "2px solid var(--primary)",
              boxShadow: "0 2px 8px rgba(39, 92, 52, 0.25)",
            }}
            aria-label={t("header.homeAriaLabel")}
            data-testid="home-btn"
          >
            <Home size={16} strokeWidth={2.6} /> {t("header.homeButton")}
          </button>
        </div>
      </header>
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
